// src/queries/esimBulk/useUploadDetails.ts

import { useQuery } from '@tanstack/react-query';
import type { PageResponse } from '@models/api';
import {
  type GetUploadHistoryDetailsParams,
  type GetUploadHistoryDetailsResponse,
  getUploadHistoryDetails,
} from '@apis/esimBulk/upload';

type UseUploadDetailsArgs = {
  page: number;
  pageSize: number;
  uploadId: number;
  params: GetUploadHistoryDetailsParams;
};

/**
 * Fetch paginated upload detail rows for a given uploadId.
 * - The query key includes page, pageSize, and onlyFailed to avoid cache collisions.
 * - The params object is never mutated.
 * - The query is enabled only when uploadId is a positive finite number.
 */
export const useUploadDetails = ({
  page,
  pageSize,
  uploadId,
  params,
}: UseUploadDetailsArgs) => {
  const onlyFailed = Boolean(params?.onlyFailed);

  return useQuery<PageResponse<GetUploadHistoryDetailsResponse>, Error>({
    queryKey: ['uploadHistoryDetails', uploadId, page, pageSize, onlyFailed],
    queryFn: () => {
      const requestParams: GetUploadHistoryDetailsParams & {
        pageNo: number;
        pageSize: number;
      } = {
        pageNo: page,
        pageSize,
        ...(onlyFailed ? { onlyFailed: true } : {}),
      };

      return getUploadHistoryDetails(uploadId, requestParams);
    },
    enabled: Number.isFinite(uploadId) && uploadId > 0,
  });
};
